

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import QuillTableBetter from "quill-table-better";
import Select from "react-select";
import 'react-quill/dist/quill.snow.css';
import '../../css/Document.css';
import '../../css/DocumentationMainPage.css';
import '../../css/DocumentationHomeComponent.css';
import '../../css/TeamWorkspaceMainPage.css';
import "../../css/ConflictResolver.css";
import '../../css/Create.css';
import '../../css/CreateWalkthrough.css';
import "../../css/QuillOverrides.css";
import * as pdfjsLib from "pdfjs-dist";
import ImageResize from "quill-image-resize-module-react";
import WorkspaceSidebar from "./WorkspaceSidebar.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";
import "../shared/UserMentionBlot";
import UserMentionDropdown from "../shared/UserMentionDropdown";
import "../../css/TaskReference.css";
import "../../css/DocEditorMakeover.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);
const Inline = Quill.import("blots/inline");
class InlineCode extends Inline {
  static create() {
    const node = super.create();
    node.setAttribute("class", "inline-code");
    return node;
  }
  static formats(node) {
    return true;
  }
}
InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";
Quill.register(InlineCode);


function EditTeamWorkspaceWalkthrough() {
    // --- All useState/useRef declarations first ---
    const { customDomain, walkthroughId } = useParams();
    const navigate = useNavigate();
    const [workspace, setWorkspace] = useState(null);
    const [walkthrough, setWalkthrough] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const { enhancedRightClickEnabled } = useQuickLinks();
    const [authResolved, setAuthResolved] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Authenticating user...");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [workspaceTheme, setWorkspaceTheme] = useState(null); 
    const [ownerIsSubscriber, setOwnerIsSubscriber] = useState(false);
    const [userRole, setUserRole] = useState(null);
    // Editable fields
    const [walkthroughName, setWalkthroughName] = useState("");
    const [description, setDescription] = useState("");
    const [tutorialCover, setTutorialCover] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [documentOrder, setDocumentOrder] = useState([]);
    const [version, setVersion] = useState(1);
    const quillRef = useRef(null);
    const { taskRefProps, userMentionProps } = useTaskReference(quillRef, { mode: "workspace", workspaceId: workspace?.id });
    // For document selection
    const [availableDocuments, setAvailableDocuments] = useState([]);
    const [selectedDocuments, setSelectedDocuments] = useState([]);

    // --- All useEffect declarations after state ---
    // Fetch available workspace documents (spaces and pages) for selection
    useEffect(() => {
        setLoadingMessage("Fetching available documents...");
        if (!workspace || !workspace.id || !userInfo) return;
        const fetchDocuments = async () => {
            try {
                const token = await userInfo.getIdToken();
                // Fetch spaces and pages
                const [spacesResponse, pagesResponse] = await Promise.all([
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Auth-Token': `Bearer ${token}`,
                            'Authorization': `${token}`,
                            'Requesting-User-Uid': userInfo.uid
                        }
                    }),
                    fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Auth-Token': `Bearer ${token}`,
                            'Authorization': `${token}`,
                            'Requesting-User-Uid': userInfo.uid
                        }
                    })
                ]);
                const spacesData = spacesResponse.status === 200 ? await spacesResponse.json() : [];
                const spaceOptions = Array.isArray(spacesData)
                    ? spacesData.map((space) => ({
                        label: space.name,
                        value: `space-${space.id}`,
                        id: space.id,
                        type: "space",
                        name: space.name
                    }))
                    : [];
                const pagesData = pagesResponse.status === 200 ? await pagesResponse.json() : [];
                const pageOptions = Array.isArray(pagesData)
                    ? pagesData.map((page) => ({
                        label: page.page_name,
                        value: `page-${page.id}`,
                        id: page.id,
                        type: "page",
                        name: page.page_name
                    }))
                    : [];
                setAvailableDocuments([...spaceOptions, ...pageOptions]);
            } catch (err) {
                // Ignore errors for now
            }
        };
        fetchDocuments();
    }, [workspace, userInfo]);

    // Add document to order
    const handleAddDocument = (doc) => {
        if (!selectedDocuments.some(d => d.id === doc.id && d.type === doc.type)) {
            setSelectedDocuments([...selectedDocuments, doc]);
        }
    };

    // Remove document from order
    const handleRemoveDocument = (idx) => {
        const updatedDocuments = [...selectedDocuments];
        updatedDocuments.splice(idx, 1);
        setSelectedDocuments(updatedDocuments);
    };

    // Move document up/down (reorder)
    const handleReorderDocuments = (startIndex, endIndex) => {
        const updated = [...selectedDocuments];
        const [removed] = updated.splice(startIndex, 1);
        updated.splice(endIndex, 0, removed);
        setSelectedDocuments(updated);
    };

    useEffect(() => {
        setLoadingMessage("Authenticating user...");
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserInfo(user);
            } else {
                setUserInfo(null);
            }
            setAuthResolved(true);
        });
        return () => unsubscribe();
    }, []);

    // Fetch workspace details by customDomain
    useEffect(() => {
        setLoadingMessage("Fetching workspace details...");
        if (!authResolved || !userInfo) return;
        const fetchWorkspaceByCustomDomain = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = await userInfo.getIdToken();
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${customDomain}/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    }
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch workspace');
                }
                const workspaceData = await response.json();
                setWorkspace(workspaceData);
                setWorkspaceTheme(workspaceData.theme); // Set theme from fetched data
                setOwnerIsSubscriber(workspaceData.owner?.is_subscriber); // Set owner subscription status
                setUserRole(workspaceData.user_role || null); // Set user role
            } catch (err) {
                setError('Error fetching workspace: ' + err.message);
                setLoading(false);
            }
        };
        if (customDomain && userInfo) {
            fetchWorkspaceByCustomDomain();
        }
    }, [customDomain, authResolved, userInfo]);

    // Theme application logic (from EditWorkspaceDocument.js)


    // Fetch walkthrough details by workspaceId and walkthroughId
    useEffect(() => {
        if (!workspace || !workspace.id || !walkthroughId || !userInfo) return;
        setLoading(true);
        setLoadingMessage("Fetching walkthrough details...");
        setError(null);
        const fetchWalkthrough = async () => {
            try {
                const token = await userInfo.getIdToken();
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/walkthroughs/${walkthroughId}/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    }
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch walkthrough');
                }
                const data = await response.json();
                setWalkthrough(data);
                // Populate form fields
                setWalkthroughName(data.walkthrough_name || "");
                setDescription(data.description || "");
                setTutorialCover(data.tutorial_cover || "");
                setIsPublic(!!data.is_public);
                setCompleted(!!data.completed);
                setDocumentOrder(data.document_order || []);
                setVersion(data.version || 1);
                
                // Convert document_order strings to full document objects for selectedDocuments
                const docOrder = data.document_order || [];
                const selectedDocs = [];
                docOrder.forEach(docKey => {
                    const [type, id] = docKey.split(/:|-/);
                    // We'll populate this when availableDocuments is loaded
                    selectedDocs.push({ type, id: parseInt(id), docKey });
                });
                setSelectedDocuments(selectedDocs);
            } catch (err) {
                setError('Error fetching walkthrough: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchWalkthrough();
    }, [workspace, walkthroughId, userInfo]);

    // Update selectedDocuments with full document info once availableDocuments is populated
    useEffect(() => {
        if (availableDocuments.length > 0 && documentOrder.length > 0) {
            const fullSelectedDocs = [];
            documentOrder.forEach(docKey => {
                const [type, id] = docKey.split(/:|-/);
                const fullDoc = availableDocuments.find(d => d.type === type && String(d.id) === id);
                if (fullDoc) {
                    fullSelectedDocs.push(fullDoc);
                }
            });
            setSelectedDocuments(fullSelectedDocs);
        }
    }, [availableDocuments, documentOrder]);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [FONT_TOOLBAR_ENTRY],
                    [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
                    [{ size: ["small", false, "large"] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ script: "sub" }, { script: "super" }],
                    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
                    [{ indent: "-1" }, { indent: "+1" }],
                    [{ align: [] }],
                    ["blockquote", "code-block"],
                    ["link", "image", "video"],
                    ["inline-code"],
                    ["formula"],
                    ["table-better"],
                    ["clean"],
                    ["image"],
                    ["undo", "redo"],
                ],
                handlers: {
                    // Optionally add undo/redo handlers if needed
                },
            },
            history: {
                delay: 1000,
                maxStack: 100,
                userOnly: true,
            },
            imageResize: {
                parchment: Quill.import("parchment"),
                modules: ["Resize", "DisplaySize"],
            },
            table: false,
            "table-better": {
                language: "en_US",
                menus: [
                    "column",
                    "row",
                    "merge",
                    "table",
                    "cell",
                    "wrap",
                    "copy",
                    "delete",
                ],
                toolbarTable: true,
                tableCreationAttributes: {
                    className: 'responsive-table',
                    style: 'width: 100%; max-width: 100%;'
                }
            },
            keyboard: {
                bindings: QuillTableBetter.keyboardBindings,
            },
        }),
        []
    );

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation checks
        if (!walkthroughName || walkthroughName.trim() === "") {
            alert("Please enter a name for your walkthrough.");
            return;
        }

        // Check that name only contains letters, numbers, spaces, periods, dashes, and underscores
        if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(walkthroughName)) {
            alert(
                "Walkthrough name must contain only letters, numbers, spaces, periods, dashes, and underscores."
            );
            return;
        }

        setIsSubmitting(true);
        setLoadingMessage("Saving changes...");
        setError(null);
        try {
            const token = await userInfo.getIdToken();
            const payload = {
                walkthrough_name: walkthroughName,
                description,
                tutorial_cover: tutorialCover,
                document_order: selectedDocuments.map(doc => `${doc.type}-${doc.id}`),
                is_public: isPublic,
                completed,
                version,
            };

            // Extract mention UIDs from Quill content and include for notifications
            const mentionedUids = (() => {
              const parser = new DOMParser();
              const parsed = parser.parseFromString(tutorialCover, 'text/html');
              const spans = parsed.querySelectorAll('[data-user-uid]');
              const uids = [...new Set([...spans].map(s => s.getAttribute('data-user-uid')).filter(Boolean))];
              return uids.filter(u => u !== userInfo.uid);
            })();
            payload.mentionedUids = mentionedUids;

            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/walkthroughs/${walkthroughId}/update/`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': userInfo.uid
                },
                body: JSON.stringify(payload)
            });
            
            if (response.ok) {
                const updated = await response.json();
                // Redirect to walkthrough view page
                navigate(`/workspace/${customDomain}/walkthroughs/${walkthroughId}`);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update walkthrough.');
            }
        } catch (err) {
            console.error("Error updating walkthrough:", err);
            alert(err.message || "An error occurred while updating the walkthrough.");
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            url: "/login",
            onClick: () => navigate("/login"),
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            url: "/register",
            onClick: () => navigate("/register"),
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            url: "https://www.youtube.com/@schoolofintology4062",
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            url: "https://discord.gg/DYkESHwgVA",
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
        },
    ];


  const loggedInOptions = userInfo && userInfo.uid ? [
      {
        label: "Submit Edits",
        icon: <i className="fas fa-paper-plane"></i>,
        onClick: handleSubmit,
      },
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => window.open("/", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Documents",
        url: "/documentation",
        onClick: () => window.open("/documentation", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Walkthroughs",
        url: "/walkthroughs",
        onClick: () => window.open("/walkthroughs", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Notebook",
        url: "/my-notebook",
        onClick: () => window.open("/my-notebook", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Workspace",
        url: "/workspaces",
        onClick: () => window.open("/workspaces", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Account Settings",
        url: `/account/${userInfo.uid}`,
        onClick: () => window.open(`/account/${userInfo.uid}`, "_blank", "noopener,noreferrer"),
      },
    ] : [];

    const menuOptions = userInfo && userInfo.uid ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

  const handleCompletedToggle = (e) => {
    setCompleted(e.target.checked);
  };

    if (loading || isSubmitting) {
    return (
      <div className="DocumentationHomeComponent__coffee-loader-div">
        <div className="coffee-cup-loader">
          <div className="cup">
            <div className="cup-body"></div>
            <div className="cup-handle"></div>
            <div className="cup-base"></div>
            <div className="cup-shadow"></div>
            <div className="steam steam1"></div>
            <div className="steam steam2"></div>
            <div className="steam steam3"></div>
          </div>
        </div>
        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
      </div>
    );
    }
    if (error) {
        return (
            <div 
                className="DocumentationMainPage__div DocEditor DocEditor--ws"
        onContextMenu={handleContextMenu}
            >
                <div className="DocumentationMainPage__main-content">
                    <p className="large">Oops! Something went wrong!</p>
                    <p className="medium">{error}</p>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    // Restrict VIEWERs from editing walkthroughs

    if (userRole === 'VIEWER') {
        return (
            <div 
                className="DocumentationMainPage__div DocEditor DocEditor--ws"
        onContextMenu={handleContextMenu}
            >
                <div className="DocumentationMainPage__main-content">
                    <p className="large">You do not have permission to edit this walkthrough.</p>
                    <p className="medium">Please contact your workspace administrator if you believe this is a mistake.</p>
                    <button
                        className="ConflictResolver__button"
                        style={{ marginBottom: '20px' }}
                        onClick={() => navigate(-1)}
                    >
                        Go Back
                    </button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    return (
        <div 
            className="TeamWorkspaceMainPage__div DocEditor DocEditor--ws"
        onContextMenu={handleContextMenu}
        >
            <WorkspaceSidebar workspaceId={workspace.id} workspaceTheme={workspaceTheme} />
            <div className="TeamWorkspaceMainPage__main-content">
                <h2>Edit Workspace Walkthrough</h2>
                <div className="Create__form-group">
                    <label>Walkthrough Name</label>
                    <input
                        type="text"
                        id="walkthroughName"
                        value={walkthroughName}
                        onChange={e => setWalkthroughName(e.target.value)}
                        maxLength={150}
                        required
                        className="Create__input-short-field"
                        placeholder="Max 150 characters"
                    />
                </div>
                <div className="Create__form-group">
                    <label>Description</label>
                    <input
                        type="text"
                        id="description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        className="Create__input-full-field"
                    />
                </div>
                {/* Document Order Section */}
                <div className="Create__form-group CreateWalkthrough__document-order-div" style={{ flexDirection: "column", marginBottom: "30px" }}>
                    <label>Select and Order Documents (Spaces/Pages):</label>
                    <div className="Create__form-group-helper-text">
                        <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                        <p className="Create__helper-text">
                            To make your walkthrough, select which documents you'd like to include in the walkthrough (by searching for them or clicking on them). Then you can also adjust the order in which documents appear in the walkthrough as well below.
                        </p>
                    </div>
                    {/* Searchable select for documents */}
                    <Select
                        options={availableDocuments.map((doc) => ({
                            value: doc,
                            label: `${doc.type}: ${doc.label || doc.name}`,
                        }))}
                        onChange={(selected) => {
                            if (selected && selected.value) handleAddDocument(selected.value);
                        }}
                        placeholder="Search for documents..."
                        isClearable
                        className="CreateWalkthrough__document-search"
                    />
                    {/* Available documents as buttons */}
                    <div className="CreateWalkthrough__available-documents">
                        {availableDocuments.filter(doc => !selectedDocuments.some(sel => sel.id === doc.id && sel.type === doc.type)).map((doc) => (
                            <button
                                key={doc.id + doc.type}
                                onClick={() => handleAddDocument(doc)}
                                className="CreateWalkthrough__add-document-button"
                            >
                                <strong>{doc.type}:</strong> {doc.label || doc.name}
                            </button>
                        ))}
                    </div>
                    {/* Selected documents with order controls */}
                    <div className="CreateWalkthrough__selected-documents">
                        {selectedDocuments.map((doc, index) => (
                            <div
                                key={doc.id + doc.type}
                                className="CreateWalkthrough__selected-document-item"
                            >
                                <span>
                                    <strong>{doc.type}:</strong> {doc.label || doc.name}
                                </span>
                                <button
                                    onClick={() => handleRemoveDocument(index)}
                                    className="CreateWalkthrough__remove-document-button"
                                >
                                    Remove
                                </button>
                                {index > 0 && (
                                    <button
                                        onClick={() => handleReorderDocuments(index, index - 1)}
                                        className="CreateWalkthrough__move-up-button"
                                    >
                                        Move Up
                                    </button>
                                )}
                                {index < selectedDocuments.length - 1 && (
                                    <button
                                        onClick={() => handleReorderDocuments(index, index + 1)}
                                        className="CreateWalkthrough__move-down-button"
                                    >
                                        Move Down
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="Create__form-group">
                    <label>Publish this walkthrough upon saving changes?</label>
                    <label className="Create__switch">
                        <input
                            type="checkbox"
                            checked={completed}
                            onChange={handleCompletedToggle}
                        />
                        <span className="Create__slider round"></span>
                    </label>
                </div>
                <div className="Create__form-group-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        Whether the walkthrough is public or not, this walkthrough will not be <strong>viewable</strong> by anyone; including added collaborators that have "Can View" permission, until it is published. Collaborators that have "Can Edit" permission can edit this walkthrough.
                    </p>
                </div>
                {/* Walkthrough Cover Content (Quill) */}
                <div className="Create__editor-div">
                    <div className="QuillOverrides__content-formatting">
                    <ReactQuill
                        ref={quillRef}
                        value={tutorialCover}
                        onChange={setTutorialCover}
                        modules={modules}
                        style={{ height: "70vh" }}
                        theme="snow"
                    />
                    <TaskReferenceDropdown {...taskRefProps} />
                    <UserMentionDropdown {...userMentionProps} />
                    </div>
                </div>
                <div className="Create__form-group-helper-text CreateWalkthrough__tutorial-cover-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        This is the cover page content for your walkthrough. It will be displayed before users begin going through the selected documents.
                    </p>
                </div>
                <button className="submit-button" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default EditTeamWorkspaceWalkthrough;